import { useState } from "react";
import { Bot, BarChart3, Users, FileText, Sparkles, Loader2, Send, Copy, CheckCircle, Target, Instagram, MessageCircle, Gift, PenTool } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api } from "../utils/api";
import DOMPurify from "dompurify";

const AIFeaturesPage = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("reports");
  
  // Reports Analysis
  const [reportsAnalysis, setReportsAnalysis] = useState("");
  const [reportsLoading, setReportsLoading] = useState(false);
  
  // Lead Scoring
  const [leadScoring, setLeadScoring] = useState("");
  const [leadScoringLoading, setLeadScoringLoading] = useState(false);
  
  // Content Generator
  const [contentType, setContentType] = useState("instagram");
  const [contentTopic, setContentTopic] = useState("");
  const [generatedContent, setGeneratedContent] = useState("");
  const [contentLoading, setContentLoading] = useState(false);
  
  // Patient Summary
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState("");
  const [patientSummary, setPatientSummary] = useState("");
  const [patientLoading, setPatientLoading] = useState(false);
  const [patientsLoaded, setPatientsLoaded] = useState(false);

  const loadPatients = async () => {
    if (patientsLoaded) return;
    try {
      const res = await api.get("/patients", token);
      setPatients(res.data.filter(p => p.name));
      setPatientsLoaded(true);
    } catch (err) { console.error(err); }
  };

  const analyzeReports = async () => {
    setReportsLoading(true);
    setReportsAnalysis("");
    try {
      const res = await api.post("/ai/analyze-reports", {}, token);
      setReportsAnalysis(res.data.analysis);
    } catch (err) { console.error(err); toast.error("خطأ في التحليل"); }
    finally { setReportsLoading(false); }
  };

  const scoreLeads = async () => {
    setLeadScoringLoading(true);
    setLeadScoring("");
    try {
      const res = await api.post("/ai/score-leads", {}, token);
      setLeadScoring(res.data.scoring);
    } catch (err) { console.error(err); toast.error("خطأ في التصنيف"); }
    finally { setLeadScoringLoading(false); }
  };

  const generateContent = async () => {
    if (!contentTopic.trim()) { toast.error("اكتب الموضوع"); return; }
    setContentLoading(true);
    setGeneratedContent("");
    try {
      const res = await api.post("/ai/generate-content", { content_type: contentType, topic: contentTopic }, token);
      setGeneratedContent(res.data.content);
    } catch (err) { console.error(err); toast.error("خطأ في التوليد"); }
    finally { setContentLoading(false); }
  };

  const getPatientSummary = async () => {
    if (!selectedPatient) { toast.error("اختر مريض"); return; }
    setPatientLoading(true);
    setPatientSummary("");
    try {
      const res = await api.post(`/ai/patient-summary/${selectedPatient}`, {}, token);
      setPatientSummary(res.data.summary);
    } catch (err) { console.error(err); toast.error("خطأ في الملخص"); }
    finally { setPatientLoading(false); }
  };

  const formatAI = (text) => {
    if (!text) return null;
    const html = text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\n/g, '<br/>');
    return <div className="text-sm leading-relaxed text-gray-700" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />;
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("تم النسخ");
  };

  const tabs = [
    { id: "reports", label: "تحليل التقارير", icon: BarChart3, color: "from-blue-500 to-cyan-500" },
    { id: "patient", label: "مساعد المريض", icon: Users, color: "from-purple-500 to-violet-500" },
    { id: "leads", label: "تصنيف العملاء", icon: Target, color: "from-amber-500 to-orange-500" },
    { id: "content", label: "توليد محتوى", icon: PenTool, color: "from-pink-500 to-rose-500" },
  ];

  return (
    <div className="animate-fade-in" data-testid="ai-features-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          أدوات الذكاء الاصطناعي
        </h1>
        <p className="text-gray-500 text-sm mt-1">تحليلات ذكية وتوليد محتوى بالذكاء الاصطناعي</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => { setActiveTab(tab.id); if (tab.id === "patient") loadPatients(); }}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${
              activeTab === tab.id
                ? `bg-gradient-to-r ${tab.color} text-white shadow-lg`
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" /> {tab.label}
          </button>
        ))}
      </div>

      {/* Reports Analysis */}
      {activeTab === "reports" && (
        <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="reports-analysis">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-500" /> تحليل التقارير الذكي
              </h3>
              <p className="text-xs text-gray-500 mt-1">تحليل شامل للإيرادات والأداء مع توصيات</p>
            </div>
            <button onClick={analyzeReports} disabled={reportsLoading} className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl text-sm font-bold hover:shadow-lg disabled:opacity-50 transition-all" data-testid="analyze-reports-btn">
              {reportsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {reportsLoading ? "يحلل..." : "حلل التقارير"}
            </button>
          </div>
          {reportsAnalysis && (
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-5 mt-4">
              <div className="flex justify-between items-start mb-3">
                <span className="text-xs font-bold text-blue-600 flex items-center gap-1"><Sparkles className="w-3 h-3" /> تحليل الذكاء الاصطناعي</span>
                <button onClick={() => copyText(reportsAnalysis)} className="text-blue-400 hover:text-blue-600"><Copy className="w-4 h-4" /></button>
              </div>
              {formatAI(reportsAnalysis)}
            </div>
          )}
        </div>
      )}

      {/* Patient Summary */}
      {activeTab === "patient" && (
        <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="patient-summary">
          <h3 className="font-bold text-gray-900 flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-purple-500" /> مساعد المريض الذكي
          </h3>
          <p className="text-xs text-gray-500 mb-4">اختر مريض للحصول على ملخص ذكي مع توصيات</p>
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <select value={selectedPatient} onChange={(e) => setSelectedPatient(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-purple-400 focus:outline-none text-sm" data-testid="patient-select-ai">
                <option value="">اختر مريض...</option>
                {patients.map(p => <option key={p.id} value={p.id}>{p.name} {p.last_name || ""} - {p.phone}</option>)}
              </select>
            </div>
            <button onClick={getPatientSummary} disabled={patientLoading || !selectedPatient} className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-purple-500 to-violet-500 text-white rounded-xl text-sm font-bold hover:shadow-lg disabled:opacity-50 transition-all" data-testid="get-summary-btn">
              {patientLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bot className="w-4 h-4" />}
              {patientLoading ? "يحلل..." : "ملخص ذكي"}
            </button>
          </div>
          {patientSummary && (
            <div className="bg-purple-50 border border-purple-100 rounded-xl p-5 mt-4">
              <div className="flex justify-between items-start mb-3">
                <span className="text-xs font-bold text-purple-600 flex items-center gap-1"><Sparkles className="w-3 h-3" /> ملخص المريض</span>
                <button onClick={() => copyText(patientSummary)} className="text-purple-400 hover:text-purple-600"><Copy className="w-4 h-4" /></button>
              </div>
              {formatAI(patientSummary)}
            </div>
          )}
        </div>
      )}

      {/* Lead Scoring */}
      {activeTab === "leads" && (
        <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="lead-scoring">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                <Target className="w-5 h-5 text-amber-500" /> تصنيف العملاء المحتملين
              </h3>
              <p className="text-xs text-gray-500 mt-1">تصنيف ذكي: ساخن / دافئ / بارد مع توصيات متابعة</p>
            </div>
            <button onClick={scoreLeads} disabled={leadScoringLoading} className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl text-sm font-bold hover:shadow-lg disabled:opacity-50 transition-all" data-testid="score-leads-btn">
              {leadScoringLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Target className="w-4 h-4" />}
              {leadScoringLoading ? "يصنّف..." : "صنّف العملاء"}
            </button>
          </div>
          {leadScoring && (
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-5 mt-4">
              <div className="flex justify-between items-start mb-3">
                <span className="text-xs font-bold text-amber-600 flex items-center gap-1"><Sparkles className="w-3 h-3" /> تصنيف ذكي</span>
                <button onClick={() => copyText(leadScoring)} className="text-amber-400 hover:text-amber-600"><Copy className="w-4 h-4" /></button>
              </div>
              {formatAI(leadScoring)}
            </div>
          )}
        </div>
      )}

      {/* Content Generator */}
      {activeTab === "content" && (
        <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="content-generator">
          <h3 className="font-bold text-gray-900 flex items-center gap-2 mb-4">
            <PenTool className="w-5 h-5 text-pink-500" /> توليد محتوى تسويقي
          </h3>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">نوع المحتوى</label>
              <div className="flex gap-2 flex-wrap">
                {[
                  { id: "instagram", label: "انستغرام", icon: Instagram },
                  { id: "offer", label: "عرض/تخفيض", icon: Gift },
                  { id: "whatsapp", label: "رسالة واتساب", icon: MessageCircle },
                  { id: "general", label: "محتوى عام", icon: FileText },
                ].map(t => (
                  <button key={t.id} onClick={() => setContentType(t.id)} className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-all ${contentType === t.id ? "bg-pink-100 text-pink-700 ring-2 ring-pink-300" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`} data-testid={`content-type-${t.id}`}>
                    <t.icon className="w-3.5 h-3.5" /> {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">الموضوع</label>
              <div className="flex gap-2">
                <input type="text" value={contentTopic} onChange={(e) => setContentTopic(e.target.value)} placeholder="مثلاً: عرض خاص على جلسات البلازما..." className="flex-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-400 focus:outline-none text-sm" data-testid="content-topic-input" />
                <button onClick={generateContent} disabled={contentLoading} className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-xl text-sm font-bold hover:shadow-lg disabled:opacity-50 transition-all" data-testid="generate-content-btn">
                  {contentLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {contentLoading ? "يولّد..." : "أنشئ المحتوى"}
                </button>
              </div>
            </div>
            {/* Quick suggestions */}
            <div className="flex gap-2 flex-wrap">
              {["عرض على زراعة الشعر", "نتائج مريض قبل وبعد", "جلسات بلازما مجانية", "تهنئة بمناسبة العيد"].map((s, i) => (
                <button key={`sug-${i}`} onClick={() => setContentTopic(s)} className="px-3 py-1 bg-gray-50 text-gray-500 rounded-lg text-xs hover:bg-pink-50 hover:text-pink-600 transition-colors border border-gray-100">{s}</button>
              ))}
            </div>
          </div>
          {generatedContent && (
            <div className="bg-pink-50 border border-pink-100 rounded-xl p-5 mt-4">
              <div className="flex justify-between items-start mb-3">
                <span className="text-xs font-bold text-pink-600 flex items-center gap-1"><Sparkles className="w-3 h-3" /> المحتوى المولّد</span>
                <button onClick={() => copyText(generatedContent)} className="text-pink-400 hover:text-pink-600 flex items-center gap-1 text-xs"><Copy className="w-3.5 h-3.5" /> نسخ</button>
              </div>
              {formatAI(generatedContent)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AIFeaturesPage;
